import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useLocation}                                     from 'react-router-dom'
import {useUserStore}                                     from '../../state'
import {studyModeFor}                                     from '../layout/AppLayout'
import {fetchAnnouncement, markAnnouncementSeen}          from '../../util/announcement'
import AnnouncementBanner                                 from './AnnouncementBanner'
import AnnouncementDialog                                 from './AnnouncementDialog'

/*
 * Decides whether the announcement is on screen (190). The only mount point.
 *
 * MOUNTED ONCE, in AuthGate beside <Analytics/> -- inside the Router but outside <Routes>, which is
 * the existing home for app-level components of this kind. AuthGate is memoised and does not
 * unmount, so this is ONE fetch per session.
 *
 * Not in AppLayout, which looks like the obvious place: AppLayout is instantiated separately for
 * each route group (/prepkit, /prep, /try, ...), so it would re-fetch every time the user crossed
 * between them.
 *
 * One fetch per session means a user already mid-session when an announcement is published sees it
 * on their next visit. That is the right trade for something this infrequent, and re-fetching on
 * window focus would be the change to make if it ever is not.
 *
 * The SERVER decides whether there is anything to show -- publish dates and who counts as an
 * existing user live in service/announcement.js. This component only asks, renders, and reports
 * back that it was seen.
 */
const AnnouncementGate = () => {
	const {user, authLoaded} = useUserStore()
	const {pathname} = useLocation()

	const [announcement, setAnnouncement] = useState(null)

	// one fetch per signed-in session; also guards against React 18 StrictMode double-invoking the
	// effect, the same guard useAchievementAlerts uses
	const requestedFor = useRef(null)

	useEffect(() => {
		if (!authLoaded) return

		// signed out: forget everything, so signing in as somebody else asks again for them
		if (!user) {
			requestedFor.current = null
			setAnnouncement(null)
			return
		}

		if (requestedFor.current === user.id) return
		requestedFor.current = user.id

		let active = true
		fetchAnnouncement().then(result => { if (active) setAnnouncement(result) })
		return () => { active = false }
	}, [authLoaded, user])

	/*
	 * Dismissal is the acknowledgement. Cleared locally first and the write is not awaited: the
	 * announcement should disappear the instant it is closed, and a failed write only means it is
	 * shown again next visit -- better than a modal that hangs on a request.
	 */
	const dismiss = useCallback(() => {
		setAnnouncement(null)
		markAnnouncementSeen()
	}, [])

	if (!announcement) return null

	/*
	 * NOT in study mode. A modal over a student mid-question is hostile, and the question and topic
	 * screens deliberately have no Navbar for the banner to sit under.
	 *
	 * This defers rather than discards: the announcement stays in state and unacknowledged on the
	 * server, so it appears as soon as they are back on a normal screen. Shares AppLayout's matcher
	 * so there is not a second copy of what counts as study mode (169).
	 */
	if (studyModeFor(pathname)) return null

	return announcement.placement === 'banner'
		? <AnnouncementBanner announcement={announcement} onDismiss={dismiss}/>
		: <AnnouncementDialog announcement={announcement} onDismiss={dismiss}/>
}

export default AnnouncementGate
